import type { AssessmentProgress, TextQuestion } from '@/features/nectar/api/models'
import { completeTextPhase, getTextQuestion, submitTextAnswer } from '@/features/nectar/api/proficiency-assessment'
import { Button, Spinner, Text } from '@/ui-kit'
import { t } from '@lingui/core/macro'
import clsx from 'clsx'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

type PhaseTextQuestionProps = {
  assessmentId: string
  onTextCompleted: () => void
  onProgress: (progress: AssessmentProgress) => void
}

export const PhaseTextQuestion = ({ assessmentId, onTextCompleted, onProgress }: PhaseTextQuestionProps) => {
  const [question, setQuestion] = useState<TextQuestion | null>(null)
  const [selectedOption, setSelectedOption] = useState<number | null>(null)
  const [answering, setAnswering] = useState(false)
  const [loading, setLoading] = useState(true)
  const [completing, setCompleting] = useState(false)
  const [timeLeft, setTimeLeft] = useState<number | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const autoSubmitRef = useRef(false)

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const fetchNextQuestion = useCallback(
    async (id: string) => {
      try {
        const res = await getTextQuestion(id)
        onProgress(res.progress)
        if (res.question) {
          setQuestion(res.question)
          setSelectedOption(null)
          clearTimer()
          if (res.question.timeLimitSec) {
            setTimeLeft(res.question.timeLimitSec)
            timerRef.current = setInterval(() => {
              setTimeLeft((prev) => {
                if (prev === null || prev <= 1) {
                  clearTimer()
                  return 0
                }
                return prev - 1
              })
            }, 1000)
          } else {
            setTimeLeft(null)
          }
          setLoading(false)
        } else {
          setCompleting(true)
          try {
            const completed = await completeTextPhase(id)
            onProgress(completed.progress)
            onTextCompleted()
          } catch {
            toast.error(t`Failed to complete text phase`)
          } finally {
            setCompleting(false)
          }
        }
      } catch {
        onTextCompleted()
      }
    },
    [clearTimer, onTextCompleted, onProgress]
  )

  const submitAnswer = useCallback(async () => {
    if (!question) return
    clearTimer()
    setTimeLeft(null)
    setAnswering(true)

    try {
      await submitTextAnswer(assessmentId, {
        questionId: question.id,
        selectedOptionIndex: selectedOption
      })
      await fetchNextQuestion(assessmentId)
    } catch {
      toast.error(t`Failed to submit answer`)
    } finally {
      setAnswering(false)
    }
  }, [assessmentId, question, selectedOption, clearTimer, fetchNextQuestion])

  // Auto-submit when timer expires
  useEffect(() => {
    if (timeLeft === 0 && !answering && !autoSubmitRef.current) {
      autoSubmitRef.current = true
      submitAnswer()
    }
    if (timeLeft !== 0) {
      autoSubmitRef.current = false
    }
  }, [timeLeft, answering, submitAnswer])

  // Initial fetch
  const initialized = useRef(false)
  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true
      fetchNextQuestion(assessmentId)
    }
  }, [assessmentId, fetchNextQuestion])

  // Cleanup timer on unmount
  useEffect(() => () => clearTimer(), [clearTimer])

  if (loading || completing) {
    return (
      <div className="flex w-full justify-center py-12">
        <div className="flex flex-col items-center gap-3">
          <Spinner />
          <Text type="body2-regular" className="text-gray-80">
            {completing ? 'Computing text results...' : 'Loading question...'}
          </Text>
        </div>
      </div>
    )
  }

  if (!question) return null

  return (
    <div
      className="mx-auto flex w-full max-w-[600px] flex-col gap-5"
      style={{ animation: 'nectar-slide-up 0.3s ease-out' }}
    >
      <div className="flex items-center justify-between gap-4">
        <Text type="body1-semibold" className="text-gray-70">
          Choose the best answer.{' '}
          {timeLeft !== null ? 'The question will auto-submit when time runs out.' : 'Take your time.'}
        </Text>
        {timeLeft !== null && (
          <Text
            type="body1-semibold"
            className={clsx(
              'shrink-0 font-mono',
              timeLeft <= 5 ? 'text-red' : timeLeft <= 10 ? 'text-orange' : 'text-gray-70'
            )}
          >
            {timeLeft}s
          </Text>
        )}
      </div>

      {/* Question card */}
      <div className="flex flex-col gap-4 rounded-2xl border border-solid border-gray-100 px-6 py-5">
        <div className="text-h5">{question.prompt}</div>

        <div className="flex flex-col gap-2">
          {question.options.map((opt, i) => (
            <button
              key={opt.text}
              type="button"
              disabled={answering}
              onClick={() => setSelectedOption(i)}
              className={clsx(
                'rounded-xl border border-solid p-3 text-left text-sm transition-colors',
                selectedOption === i ? 'border-green bg-green/5' : 'border-gray-100 hover:bg-gray-100/50'
              )}
            >
              {opt.text}
            </button>
          ))}
        </div>
      </div>

      <Button onClick={submitAnswer} isDisabled={selectedOption === null || answering} className="w-full">
        {answering ? 'Submitting...' : 'Submit Answer'}
      </Button>
    </div>
  )
}
